import { RefObject, useCallback, useEffect, useRef } from 'react';
import { FormikProps } from 'formik';
import ReactQuill from 'react-quill-new';

import { IComposePanelFields } from './compose-panel.types';
import { COMPOSE_FORM_ID } from './compose-panel.constants';
import { InsertTarget, insertIntoQuill, insertIntoTextField, resolveInsertTarget } from './compose-panel.insert';
import {
  deliveryOptionRequiresEmail,
  deliveryOptionRequiresSMS
} from '../../../lib/helpers/communication.helpers';
import { SMS_BODY_MAX } from '../../../lib/helpers/sms.helpers';

interface UseVariableInsertionArgs {
  initialValues: IComposePanelFields;
}

interface UseVariableInsertionReturn {
  quillRef: RefObject<ReactQuill>;
  onFieldFocus: (target: InsertTarget) => void;
  onQuillSelectionCapture: (range: ReactQuill.Range) => void;
  insertToken: (token: string, form: FormikProps<IComposePanelFields>) => void;
}

// HOOK: owns the compose panel's variable-insertion targeting — the editor ref,
// the last-focused field + caret refs, and the insert-at-target handler.
export const useVariableInsertion = ({
  initialValues
}: UseVariableInsertionArgs): UseVariableInsertionReturn => {
  // Variable-insertion targeting: a token insert goes into the content field
  // that last held focus, at the email editor's most recent caret position.
  const quillRef = useRef<ReactQuill>(null);
  const lastInsertTargetRef = useRef<InsertTarget | null>(null);
  // Quill reports a null range whenever the editor blurs; retaining the last
  // non-null range keeps an insert landing at the caret after a click elsewhere.
  const lastQuillRangeRef = useRef<ReactQuill.Range>(null);

  const handleFieldFocus = useCallback((target: InsertTarget) => {
    lastInsertTargetRef.current = target;
  }, []);

  const handleQuillSelectionCapture = useCallback((range: ReactQuill.Range) => {
    if (range) lastQuillRangeRef.current = range;
  }, []);

  // A new compose document (draft switch / new / compose-another) invalidates
  // the caret and last-focused target captured against the previous document.
  useEffect(() => {
    lastInsertTargetRef.current = null;
    lastQuillRangeRef.current = null;
  }, [initialValues]);

  // Executes a panel insert at the resolved target (last-focused if still
  // rendered, else visible default). Text-field targets restore the caret.
  const handleInsertToken = useCallback((token: string, form: FormikProps<IComposePanelFields>) => {
    // No content mutation while a submit is in flight — the gate validates a frozen
    // snapshot, so a mid-submit insert would desync the report from the sent text.
    if (form.isSubmitting) return;
    const target = resolveInsertTarget(lastInsertTargetRef.current, {
      usesEmail: deliveryOptionRequiresEmail(form.values.deliveryOption),
      usesSMS: deliveryOptionRequiresSMS(form.values.deliveryOption)
    });

    if (target === 'bodyHtml') {
      const editor = quillRef.current && quillRef.current.getEditor();
      if (editor) {
        insertIntoQuill(editor, token, lastQuillRangeRef.current);
        return;
      }
      // Editor unmounted (preview showing) — append to the field value instead.
      const { nextValue } = insertIntoTextField(
        form.values.bodyHtml, form.values.bodyHtml.length, form.values.bodyHtml.length, token
      );
      form.setFieldValue('bodyHtml', nextValue);
      return;
    }

    const formEl = document.getElementById(COMPOSE_FORM_ID);
    const input = formEl && formEl.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${target}"]`);
    const value = form.values[target];
    // Only the still-focused field (kept focused by the chip's preventDefault) has a
    // live caret; an unfocused target appends at the end, not at a stale index 0.
    const focused = input === document.activeElement ? input : null;
    const selectionStart = focused ? focused.selectionStart ?? value.length : value.length;
    const selectionEnd = focused ? focused.selectionEnd ?? value.length : value.length;
    const { nextValue, nextCaret } = insertIntoTextField(value, selectionStart, selectionEnd, token);
    // SMS body honors the same hard cap typed input gets — setFieldValue isn't
    // bound by the textarea's maxLength.
    if (target === 'smsBody' && nextValue.length > SMS_BODY_MAX) return;
    form.setFieldValue(target, nextValue);
    if (input) {
      // Restore focus/caret after the controlled re-render commits the value.
      window.requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(nextCaret, nextCaret);
      });
    }
  }, []);

  return {
    quillRef,
    onFieldFocus: handleFieldFocus,
    onQuillSelectionCapture: handleQuillSelectionCapture,
    insertToken: handleInsertToken
  };
};
